import isEmpty from 'lodash/isEmpty';
import {Component} from 'panel';
import {produce} from 'immer';

import {BannerAction, BannerSize, BannerTheme} from 'common/components/banner/types';
import {reporter} from 'common/report/error-reporting';
import {Persistence} from 'common/report/persistence';
import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {unique} from 'common/util/array';
import {getFeatureFlagUrlOverrides} from 'common/util/url';
import {ToastType} from 'common/components/toast/types';
import {addToast} from 'common/components/toast-area/toast-manager';

import {openMediaModal} from '../tutorial-video-modal';
import {getBanners, GetBannersResponse} from './api';
import {BannerEventNames, BannerEventProps, REDIRECT_BANNERS, RedirectBannerUrlParams} from './constants';
import {ReportBanner, ReportBannersState} from './types';
import {template} from './template';
import css from './index.inline.styl';

export class ReportBanners extends Component<ReportBannersState> {
  private persistence: Persistence;

  static get attrsSchema() {
    return {
      report: `string`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        banners: [],
      },
      helpers: {
        handleBannerAction: (ev, bannerIdx: number) => {
          const {actionName} = ev.detail;

          if (actionName === BannerAction.Dismiss) {
            this.hideBanner(bannerIdx, true);
          } else if (actionName === BannerAction.Tutorial) {
            openMediaModal((ev) => {
              this.addErrorToast(ev.detail.message);
            });
          } else if (actionName !== BannerAction.None) {
            this.hideBanner(bannerIdx);
          }
        },
        getReportName: () => {
          return this.attr(`report`) || getReportName();
        },
        areBannersVisible: (): boolean => {
          return this.state.banners.filter((banner) => banner.visible).length > 0;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    if (getProjectId() && getWorkspaceId()) {
      this.syncBanners();
    } else {
      // if projectId and workspaceId are not yet available, that means they
      // are coming from mp.report.globals, so we need to sync after DOMContentLoaded
      // since those globals are initialized after mp-report-banner
      window.addEventListener(
        `DOMContentLoaded`,
        () => {
          this.syncBanners();
        },
        {once: true},
      );
    }
  }

  async syncBanners() {
    const projectId = getProjectId();
    this.persistence = new Persistence(`banners:${projectId}`);

    // update static banners first before requesting from API
    this.update({banners: this.getStaticBanners()});
    // don't load any banners if you don't have a project id as it they won't load properly
    if (projectId) {
      try {
        const bannersResponse = await getBanners({
          projectId,
          workspaceId: getWorkspaceId(),
          reportName: this.helpers.getReportName(),
        });

        this.update({
          banners: unique([...this.state.banners, ...this.getBannersFromResponse(bannersResponse)], {
            // ensure duplicate banners (with same title+message) are not added
            // duplicates can occur when switching quickly between reports with no page reload
            hash: (banner) => `${banner.title}|${banner.message}`,
          }),
        });
      } catch (err) {
        reporter.error(err);
      }
    }
  }

  /**
   * Get any banners that don't require making request to banners async endpoint (e.g. based on url params).
   */
  getStaticBanners(): ReportBanner[] {
    const featureFlagsBanner = this.getFeatureFlagsUrlOverrideBanner();
    return [...this.getRedirectBanners(), ...(featureFlagsBanner ? [featureFlagsBanner] : [])];
  }

  /**
   * Get banners to be shown based on query params.
   * Typical use case is showing a confirmation message after a redirect flow.
   */
  getRedirectBanners(): ReportBanner[] {
    const url = new URL(window.location.toString());
    const banners = Object.values(RedirectBannerUrlParams)
      .map((redirectBannerName) => {
        if (url.searchParams.has(redirectBannerName)) {
          return REDIRECT_BANNERS[redirectBannerName];
        }
        return null;
      })
      .filter(Boolean);

    return banners;
  }

  getFeatureFlagsUrlOverrideBanner(): ReportBanner {
    const featureFlagUrlOverrides = getFeatureFlagUrlOverrides();
    if (isEmpty(featureFlagUrlOverrides)) {
      return null;
    }

    const flagMessageParts = Object.entries(featureFlagUrlOverrides).map(([flag, isEnabled]) => {
      return isEnabled ? `"${flag}"` : `"${flag}" (disabled)`;
    });
    const message =
      `Url feature flag overrides: ${flagMessageParts.join(`, `)}.` +
      ` It's recommended to set feature flag overrides with HTTP headers.` +
      ` [More info](https://www.notion.so/mpspeed/Project-feature-flag-overrides-4ed8248452744361ac0bab35362a1f58)`;
    return {
      message,
      name: `url-feature-flag-overrides`,
      type: BannerTheme.Info,
      size: BannerSize.Medium,
      ctas: [{actionName: BannerAction.Dismiss, text: `Dismiss`}],
      visible: true,
    };
  }

  bannersResponseToReportBanners(bannersResponse: GetBannersResponse): ReportBanner[] {
    return bannersResponse.map(
      (bannerData) =>
        ({
          type: bannerData.type,
          ctas: bannerData.ctas,
          message: bannerData.message,
          name: bannerData.name || bannerData.title,
          persistDismissKey: bannerData.persistDismissKey,
          size: bannerData.size,
          title: bannerData.title,
          onShow: bannerData.onShow,
          visible: bannerData.persistDismissKey && this.persistence.get(bannerData.persistDismissKey) ? false : true,
          trackingType: bannerData.trackingType,
        } as ReportBanner),
    );
  }

  getBannersFromResponse(bannersResponse: GetBannersResponse) {
    const banners = this.bannersResponseToReportBanners(bannersResponse).filter(
      (banner) => banner.visible && banner.type,
    );

    if (banners.length) {
      mixpanel.track(BannerEventNames.BannersShown, {
        [BannerEventProps.NumBannersShown]: banners.length,
        [BannerEventProps.BannerType]: banners.map((banner) => banner.type),
        [BannerEventProps.BannerMessage]: banners.map((banner) => banner.message),
        [BannerEventProps.BannerName]: banners.map((banner) => banner.name),
        [BannerEventProps.BannerTitle]: banners.map((banner) => banner.title),
      });
      banners.forEach((banner) => {
        this.trackBannerEvent(BannerEventNames.BannerShown, banner);
        if (banner.onShow) {
          const {trackEventName, trackProps} = banner.onShow;
          mixpanel.track(trackEventName, trackProps);
        }
      });
    }

    return banners;
  }

  trackBannerEvent(eventName: string, banner: ReportBanner) {
    mixpanel.track(eventName, {
      [BannerEventProps.BannerType]: banner.type,
      [BannerEventProps.BannerMessage]: banner.message,
      [BannerEventProps.BannerName]: banner.name,
      [BannerEventProps.BannerTitle]: banner.title,
      [BannerEventProps.ReportName]: this.helpers.getReportName(),
      [BannerEventProps.ProjectId]: getProjectId(),
      [BannerEventProps.WorkspaceId]: getWorkspaceId(),
    });
  }

  hideBanner(bannerIdx: number, dismissed = false) {
    const {banners} = this.state;
    const banner = banners[bannerIdx];

    if (dismissed) {
      if (banner.persistDismissKey) {
        this.persistence.set(banner.persistDismissKey, Date.now());
      }

      // clean up the URL param, if there was one for this banner
      if (Object.values<string>(RedirectBannerUrlParams).includes(banner.name)) {
        const url = new URL(window.location.toString());
        url.searchParams.delete(banner.name);
        window.history.replaceState(null, null, url.href);
      }
    }
    this.update({
      banners: produce(banners, (banners) => {
        banners[bannerIdx].visible = false;
      }),
    });
    this.trackBannerEvent(BannerEventNames.BannerHidden, banner);
  }

  addErrorToast(message) {
    addToast({
      type: ToastType.ERROR,
      isError: true,
      message,
    });
  }
}

export const ReportBannersEl = defineAndMakeJsxFactory(`mp-report-banners`, ReportBanners);
export const ReportBannersReact = panel2react(`mp-report-banners`, ReportBanners);
